import { Link } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LuArrowRight } from 'react-icons/lu';
import { ApiError } from '@/utils/apiFetch';

// 401/403 はプラン制限・権限不足であって障害ではないため、5xx と同じ文言にしない (Issue #3329)
function describe(error: unknown): { status: number; title: string; description: string } {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return {
        status: error.status,
        title: 'ご利用いただけません',
        description:
          'このページを表示する権限がありません。ご契約プランに含まれていないか、アクセス権限が付与されていない可能性があります。',
      };
    }
    if (error.status === 404) {
      return {
        status: error.status,
        title: 'ページが見つかりません',
        description: 'お探しのページは削除されたか、URLが変更された可能性があります。',
      };
    }
    if (error.status < 500) {
      return {
        status: error.status,
        title: 'リクエストを処理できません',
        description: '入力内容をご確認のうえ、もう一度お試しください。',
      };
    }
    return {
      status: error.status,
      title: 'エラーが発生しました',
      description: '申し訳ございません。予期しないエラーが発生しました。',
    };
  }

  return {
    status: 500,
    title: 'エラーが発生しました',
    description: '申し訳ございません。予期しないエラーが発生しました。',
  };
}

export default function ErrorPage({ error, reset }: ErrorComponentProps) {
  const isLocalhost =
    typeof window !== 'undefined' && window.location.hostname.includes('localhost');
  const { status, title, description } = describe(error);
  // 4xx は再試行しても結果が変わらないため再試行ボタンを出さない
  const isClientError = status >= 400 && status < 500;

  useEffect(() => {
    try {
      localStorage.removeItem('providedJobPostsSearchQuery');
      localStorage.removeItem('candidateProfilesSearchQuery');
    } catch (error) {
      console.error('Failed to clear search query from localStorage:', error);
    }
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#e7e7e7] p-10">
      <div className="relative flex min-h-[728px] w-full max-w-[1136px] flex-col items-center overflow-hidden rounded-2xl bg-white pb-10">
        {/* Background blur effects */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute top-[-193.38px] left-[591.79px] h-[630.18px] w-[796.91px] rounded-full bg-[#f6f3ff]"
            style={{ filter: 'blur(200px)' }}
          />
          <div
            className="absolute top-[395.79px] left-[-181.4px] h-[409.13px] w-[517.38px] rounded-full bg-[#edf8ff]"
            style={{ filter: 'blur(200px)' }}
          />
        </div>

        {/* Header with logo */}
        <header className="z-10 flex w-full pt-10 pl-10">
          <img src="/logo/agenthub.svg" alt="エージェントハブ" className="w-[317px]" />
        </header>

        {/* Main content */}
        <div className="z-10 mt-16 flex w-full max-w-[800px] flex-col items-center px-10">
          <div className="text-center">
            <h1 className="text-8xl font-bold text-red-400">{status}</h1>
            <p className="mt-6 text-base font-bold">{title}</p>
            <p className="mt-2 text-sm text-gray-600">{description}</p>
          </div>

          {/* Error details - localhost環境でのみ表示 */}
          {isLocalhost && error instanceof Error && (
            <div className="mt-6 w-full rounded-lg bg-red-50 p-4 text-left">
              <p className="text-sm font-semibold text-red-800">エラー詳細:</p>
              <p className="mt-2 text-sm text-red-700">{error.message}</p>
              {error.stack && (
                <pre className="mt-4 max-h-60 overflow-auto rounded bg-red-100 p-2 text-xs text-red-900">
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="mt-8 flex gap-4">
            {reset && !isClientError && (
              <button
                onClick={() => reset()}
                className="flex items-center justify-center gap-2 rounded-[5px] border-none bg-[#005fff] px-4 py-[14px] text-base leading-normal font-bold tracking-wider text-white hover:opacity-80"
              >
                再試行
              </button>
            )}
            <Link to="/agent">
              <button className="flex items-center justify-center gap-2 rounded-[5px] border border-[#005fff] bg-white px-4 py-[14px] text-base leading-normal font-bold tracking-wider text-[#005fff] hover:opacity-80">
                <span>ダッシュボードへ</span>
                <LuArrowRight className="size-3" aria-hidden />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
